import { Achievement, UserAchievement } from '@vae/server/dist/client'
import React, { FC, useEffect, useState } from 'react'
import { AchievementsHook } from '../../hooks/useAchievements'
import { useAwardPointsAnimation } from '../../hooks/useAwardPointsAnimation'
import { translateAchievement } from '../../utils/achievement/translatedAchievement'
import AchievementProgressBar from './AchievementProgressBar'
import PointsBadge from './PointsBadge'
import ProgressCircle from './ProgressCircle'

export interface AchievementCardProps {
	pointsContainer: HTMLDivElement
	userAchievement: UserAchievement & { achievement: Achievement }
	setPointsAwarded: AchievementsHook['setPointsAwarded']
	setLastObservedStep: AchievementsHook['setLastObservedStep']
}

const AchievementCard: FC<AchievementCardProps> = ({
	pointsContainer,
	userAchievement,
	setPointsAwarded,
	setLastObservedStep,
}) => {
	const {
		achievement,
		step: currentStep,
		isCompleted,
		pointsAwarded: backendPointsAwarded,
		lastObservedStep: backendLastObservedStep,
	} = userAchievement
	const { title, description, points, steps: goal } = translateAchievement(achievement)

	const [localPointsAwarded, setLocalPointsAwarded] = useState(backendPointsAwarded)
	const [localLastObservedStep, setLocalLastObservedStep] = useState(backendLastObservedStep)

	useEffect(() => {
		setLocalPointsAwarded(backendPointsAwarded)
	}, [backendPointsAwarded])

	useEffect(() => {
		setLocalLastObservedStep(backendLastObservedStep)
	}, [backendLastObservedStep])

	const pointsBadgeRef = useAwardPointsAnimation({
		pointsAwarded: localPointsAwarded,
		isCompleted,
		elementProps: {
			pointsContainer,
			points,
			setPointsAwarded: (id: number) => {
				setLocalPointsAwarded(true)
				setPointsAwarded(id)
			},
			userAchievement,
		},
	})

	const isMultiStep = goal > 1

	return (
		<div className='flex flex-row gap-4 items-center'>
			<div className='flex justify-between flex-col flex-grow'>
				<span className='text-lg font-semibold tracking-wide text-text-dark dark:text-white'>
					{title}
				</span>

				{description && (
					<span className='text-md text-text-grey-200 dark:text-[#dcdcdc]'>{description}</span>
				)}

				{isMultiStep && (
					<AchievementProgressBar
						lastObservedStep={localLastObservedStep}
						setLastObservedStep={(id, step) => {
							setLocalLastObservedStep(step)
							setLastObservedStep(id, step)
						}}
						currentStep={currentStep}
						goal={goal}
						achievementId={userAchievement.id}
					/>
				)}

				<PointsBadge ref={pointsBadgeRef} points={points} isCompleted={isCompleted} />
			</div>

			<ProgressCircle
				step={currentStep}
				steps={goal}
				isMultiStep={isMultiStep}
				isCompleted={isCompleted}
			/>
		</div>
	)
}

export default AchievementCard
